import { AccessContext, BrandConfig, BrandSource } from "./types";
import { matchesDomain, matchesRules, matchesUrlHost } from "./matchers";

// Marca default: reproduz o visual atual. match vazio = só fallback.
export const defaultBrand: BrandConfig = {
  id: "default",
  match: {},
  portalName: "Autenticação - Atem",
  assets: { logo: "/images/atem-logotipo.png" },
  schemes: {
    light: { primary: { main: "#D91F05" }, secondary: { main: "#736161" } },
  },
  defaultMode: "light",
};

// Portal da Nave: identificado pelo HOST de redirecionamento (?url), por prefixo de label
// `portalnave*` — casa `portalnave` e ambientes com hífen (`portalnave-dev`,
// `portalnave-dev-local`, `portalnave-qa`...). Exige o hífen, então `portalnavefake` NÃO
// casa. Usamos urlHosts (não rules livre) porque o valor é o hostname do redirect, casado
// contra allowlist fechada: não é forjável como um ?rules arbitrário seria. Não há domínio
// dedicado de autenticador para a Nave.
export const naveBrand: BrandConfig = {
  id: "nave",
  match: { urlHosts: ["portalnave*"] },
  portalName: "Portal da Nave",
  assets: {
    logo: "/brands/nave/logo-navemazonia.png",
    logoIcon: "/brands/nave/logo-icon.png",
    loginBackground: "/brands/nave/login-background.jpg",
  },
  schemes: {
    light: {
      // dark = hover do protótipo (mesma cor de light); sem ele, o hover herdaria o vermelho
      // escuro da Atem via deep-merge. Ver createBrandTheme (augmentColor).
      primary: { main: "#0D265E", light: "#1A3A7A", dark: "#1A3A7A" },
      secondary: { main: "#F39200" },
    },
  },
  defaultMode: "light",
  typography: { fontFamily: ["Inter", "system-ui", "sans-serif"].join(",") },
  shape: { borderRadius: 8 }, // cantos do protótipo (8px); vira multiplicador do sx MUI
  login: {
    layout: "split",
    eyebrow: "PORTAL DO CLIENTE DE ARMAZENAGEM",
    headline: "Saldos, veículos e notas fiscais do seu produto no terminal.",
    subtitle:
      "Consulte o saldo por tanque e por lote, acompanhe cargas e descargas e agende a chegada dos caminhões.",
    copyright: "© 2026 Navemazônia",
    backgroundOverlayOpacity: 0.85, // tom primary cobrindo a imagem, deixando-a sutil
  },
};

// Marcas não-default, em ordem de avaliação. Default é o fallback.
const brands: BrandConfig[] = [naveBrand];

// Único dono da precedência/match: domínio > rules > url(host) > default.
export function resolveBrand(ctx: AccessContext): BrandConfig {
  const byDomain = brands.find((b) => matchesDomain(ctx.host, b.match));
  if (byDomain) return byDomain;

  const byRules = brands.find((b) => matchesRules(ctx.rules, b.match));
  if (byRules) return byRules;

  const byUrl = brands.find((b) => matchesUrlHost(ctx.urlHost, b.match));
  if (byUrl) return byUrl;

  return defaultBrand;
}

// True se o host casa o domínio dedicado de alguma marca não-default. Usado pelo
// travamento de fluxo em CurrentApplication (mesmo matcher do resolveBrand).
export function hasDedicatedBrandDomain(host: string): boolean {
  return brands.some((b) => matchesDomain(host, b.match));
}

// Fonte estática; BrandSource delega ao resolveBrand (sem segunda implementação).
export const brandSource: BrandSource = {
  resolve: resolveBrand,
};
